// Themes (E09-S1-T6, the slice E12-S1-T4 needs): a theme is a block of token overrides in
// src/styles/themes/<id>.css, selected by data-theme on a root element. Everything below that root —
// the hub's frame, a mounted surface, a portalled popover — reads the same tokens, so one attribute
// recolours all of it with no component change.
//
// The default theme is tokens.generated.css itself (mockups/kit/tokens.css) and needs no block.
// A theme id that is not in themeIds falls back to the default, and the fallback is reported: a
// tenant whose configured theme does not exist sees the default rather than a half-styled page, and
// someone hears about it.
//
// A theme also carries two brand slots (R-PS-9): the product name and a logo. The logo is inline SVG
// shape data drawn in currentColor (components/brand renders it), never an address: it is on screen
// with the first frame and makes no request. e2e/theme.spec.ts opens every gallery story under every
// theme and checks the colours, the name and the logo change, and that text keeps 4.5:1 on its ground.
export const DEFAULT_THEME = "default";
/** Every theme the surface ships; each id but the default has a block in styles/themes/. */
export const themeIds = [DEFAULT_THEME, "neutral"];
/**
 * The brand of each theme. The default is Waypoint and the mark the mockups draw in the Container's
 * title bar (mockups/container.js, icon "waypoint"). The neutral brand is a placeholder for the proof:
 * a name that is plainly a sample, and a plain hexagon mark drawn for it.
 */
export const themeBrands = {
    default: {
        productName: "Waypoint",
        logo: [
            { kind: "circle", cx: 12, cy: 12, r: 8 },
            { kind: "circle", cx: 12, cy: 12, r: 2.8, filled: true },
        ],
    },
    neutral: {
        productName: "Sample Brand",
        logo: [
            { kind: "path", d: "M12 3.5l7.4 4.25v8.5L12 20.5l-7.4-4.25v-8.5z" },
            { kind: "path", d: "M12 8.6l3.4 3.4-3.4 3.4-3.4-3.4z", filled: true },
        ],
    },
};
/** The brand of a theme; a missing or unknown id has the default's, as applyTheme falls back. */
export function themeBrand(id) {
    return themeBrands[isThemeId(id) ? id : DEFAULT_THEME];
}
export function isThemeId(id) {
    return typeof id === "string" && themeIds.includes(id);
}
/** A theme id as it may be written to a log: an id's shape, or a placeholder. Never free text. */
export function loggableThemeId(requested) {
    return /^[a-z][a-z0-9-]{0,39}$/.test(requested) ? requested : "(not a theme id)";
}
function warnUnknown(requested) {
    console.warn(`[waypoint] theme "${loggableThemeId(requested)}" is not a theme the surface ships; using the default theme.`);
}
/**
 * Selects a theme for everything under `root` by setting data-theme. A missing id is the default; an
 * unknown one is the default and is reported. Returns the theme applied.
 */
export function applyTheme(root, id, options = {}) {
    let applied = DEFAULT_THEME;
    if (isThemeId(id))
        applied = id;
    else if (id !== null && id !== undefined)
        (options.onUnknown ?? warnUnknown)(id);
    root.setAttribute("data-theme", applied);
    return applied;
}
//# sourceMappingURL=theme.js.map